import React, { useCallback, useContext, useEffect, useState } from "react";
import "./LoginPop.scss";
import "../GoogleSignIn/GoogleSignIn.scss";
import { ReactComponent as Close } from '../../media/common/close.svg';
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import axios from 'axios';
import AuthContext from "../../context/AuthContext";
import SnackbarContext from "../../context/SnackbarContext";
import Button from "../Button/Button";
import GoogleSignIn from "../GoogleSignIn/GoogleSignIn";
import { API_BASE_URL, GOOGLE_CLIENT_ID } from '../../config';


export default function LoginPop({ toggleShowLogin }) {

    const { setSnackbar } = useContext(SnackbarContext);
    const { loginUser, googleLogin, verifyTwoFactor, resendTwoFactor } = useContext(AuthContext);
    const navigate = useNavigate();
    const [inactiveEmail, setInactiveEmail] = useState(null);
    const [resending, setResending] = useState(false);
    // 2FA challenge state: when set, password form is hidden and code input is shown.
    const [twoFactor, setTwoFactor] = useState(null); // { preAuthToken, emailMasked }
    const [code, setCode] = useState('');
    const [rememberDevice, setRememberDevice] = useState(true);
    const [verifying, setVerifying] = useState(false);

    const { register, handleSubmit, formState: { isSubmitting } } = useForm();

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') toggleShowLogin(); };
        document.addEventListener('keydown', onKey);
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = previousOverflow;
        };
    }, [toggleShowLogin]);

    const finishLogin = () => {
        navigate('/library');
        setSnackbar({ content: "تم تسجيل الدخول بنجاح", success: true });
        toggleShowLogin();
    };

    const onSubmit = async (data) => {
        const r = await loginUser(data);
        if (r?.status === 200 && r?.data?.code === '2fa_required') {
            setTwoFactor({ preAuthToken: r.data.pre_auth_token, emailMasked: r.data.email_masked });
            return;
        }
        if (r?.status === 200) {
            finishLogin();
            return;
        }

        const code = r?.data?.code || r?.response?.data?.code;
        const detail = r?.data?.detail || r?.response?.data?.detail;
        const status = r?.status ?? r?.response?.status;

        if (code === 'inactive') {
            setInactiveEmail(data.username);
            setSnackbar({ content: detail, success: false });
        } else if (status === 401) {
            setSnackbar({ content: "الرجاء التأكد من اسم المستخدم وكلمة المرور", success: false });
        } else {
            setSnackbar({ content: detail || "حدث خطأ", success: false });
        }
    };

    const onVerifyCode = async (e) => {
        e.preventDefault();
        if (!twoFactor || code.length < 6) return;
        setVerifying(true);
        const r = await verifyTwoFactor(twoFactor.preAuthToken, code, rememberDevice);
        setVerifying(false);
        if (r?.status === 200 && r?.data?.access) {
            finishLogin();
            return;
        }
        const detail = r?.data?.detail || r?.response?.data?.detail || 'الرمز غير صحيح';
        const errCode = r?.data?.code || r?.response?.data?.code;
        setSnackbar({ content: detail, success: false });
        if (errCode === 'preauth_expired' || errCode === 'too_many_attempts') {
            // Session is dead; return to password step.
            setTwoFactor(null);
            setCode('');
        }
    };

    const onResendCode = async () => {
        if (!twoFactor) return;
        setResending(true);
        const r = await resendTwoFactor(twoFactor.preAuthToken);
        setResending(false);
        if (r?.status === 200 && r?.data?.pre_auth_token) {
            setTwoFactor({
                preAuthToken: r.data.pre_auth_token,
                emailMasked: r.data.email_masked || twoFactor.emailMasked,
            });
            setCode('');
            setSnackbar({ content: 'تم إرسال رمز جديد إلى بريدك', success: true });
        } else {
            setSnackbar({ content: 'تعذر إرسال الرمز', success: false });
        }
    };

    const onGoogleCredential = useCallback(async (credential) => {
        const r = await googleLogin(credential);
        if (r?.status === 200) {
            setSnackbar({ content: 'تم تسجيل الدخول عبر Google', success: true });
            navigate('/library');
            toggleShowLogin();
        } else {
            const msg = r?.data?.detail || 'تعذر تسجيل الدخول عبر Google';
            setSnackbar({ content: msg, success: false });
        }
    }, [googleLogin, navigate, setSnackbar, toggleShowLogin]);

    const resend = async () => {
        if (!inactiveEmail) return;
        setResending(true);
        try {
            await axios.post(`${API_BASE_URL}/api/users/resend-verification/`, { email: inactiveEmail });
            setSnackbar({ content: 'تم إرسال رابط جديد إلى بريدك', success: true });
        } catch {
            setSnackbar({ content: 'تعذر إرسال البريد', success: false });
        } finally {
            setResending(false);
        }
    };

    return (
        <div className="popup" onClick={toggleShowLogin} role='dialog' aria-modal='true' aria-label='تسجيل الدخول'>
            <div className="popup-container" onClick={(e) => e.stopPropagation()}>
                <div className="popup-header">
                    <button className="popup-close" aria-label='إغلاق' onClick={toggleShowLogin}>
                        <Close />
                    </button>
                    <span className="popup-title">{twoFactor ? 'التحقق الثنائي' : 'تسجيل الدخول'}</span>
                </div>

                {!twoFactor && GOOGLE_CLIENT_ID && (
                    <div className='popup-google'>
                        <GoogleSignIn text='signin_with' onCredential={onGoogleCredential} />
                        <div className='auth-divider'>أو</div>
                    </div>
                )}

                {!twoFactor && (
                    <form className="popup-form" onSubmit={handleSubmit(onSubmit)}>
                        <input
                            {...register("username", { required: true })}
                            type="text"
                            placeholder="اسم المستخدم أو البريد"
                            autoComplete="username"
                        />
                        <input
                            {...register("password", { required: true })}
                            type="password"
                            placeholder="كلمة المرور"
                            autoComplete="current-password"
                        />
                        <Button placeholder={isSubmitting ? 'جارٍ الدخول…' : 'تسجيل دخول'} disabled={isSubmitting} />
                    </form>
                )}

                {twoFactor && (
                    <form className="popup-form" onSubmit={onVerifyCode}>
                        <p className='popup-2fa-hint'>
                            أرسلنا رمزًا مكوّنًا من ٦ أرقام إلى <strong>{twoFactor.emailMasked}</strong>. أدخله أدناه.
                        </p>
                        <input
                            type="text"
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            placeholder="------"
                            value={code}
                            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            autoFocus
                        />
                        <label className='popup-remember'>
                            <input type='checkbox' checked={rememberDevice} onChange={(e) => setRememberDevice(e.target.checked)} />
                            <span>لا تطلب الرمز على هذا الجهاز لمدة ٣٠ يومًا</span>
                        </label>
                        <Button
                            placeholder={verifying ? 'جارٍ التحقق…' : 'تأكيد'}
                            disabled={verifying || code.length < 6}
                        />
                        <button
                            type='button'
                            className='popup-resend'
                            onClick={onResendCode}
                            disabled={resending}
                        >
                            {resending ? 'جارٍ الإرسال…' : 'إعادة إرسال الرمز'}
                        </button>
                    </form>
                )}

                {inactiveEmail && !twoFactor && (
                    <div className='popup-inactive'>
                        <p>حسابك لم يُفعَّل بعد. تحقق من بريدك أو</p>
                        <button type='button' className='popup-resend' onClick={resend} disabled={resending}>
                            {resending ? 'جارٍ الإرسال…' : 'أعد إرسال رابط التفعيل'}
                        </button>
                    </div>
                )}

                {!twoFactor && (
                    <div className="popup-footer">
                        <p>ليس لديك حساب؟ <Link id="popup-to-new-account" to="/register" onClick={toggleShowLogin}>إنشاء حساب جديد</Link></p>
                    </div>
                )}
            </div>
        </div>
    );
}
